/**
 * Authentication Request Interceptor Plugin
 *
 * Provides a global $fetch interceptor that handles token expiration checking
 * and refresh logic for all API requests.
 *
 * Features:
 * - Checks if the token is about to expire before each request (within 5 minutes)
 * - Triggers token refresh when needed, blocking the outgoing request until fresh
 * - Synchronizes token refreshes across tabs (single tab takes ownership)
 * - Always includes credentials for cookie-based authentication
 * - Robust 401 fallback: retries requests after refreshing token on unexpected 401
 *
 * Flow:
 * 1. Component calls $fetch or useSecuredApi
 * 2. Interceptor reads tokenExpiresAt (ms) from store
 * 3. If token > 5 min left, continues with credentials: 'include'
 * 4. If token <= 5 min left: triggers refresh, waits, continues on success
 * 5. On refresh failure: clears auth state and redirects to login
 * 6. Backend receives request with valid token
 *
 * 401 Fallback Flow (handles clock drift, network delay, etc.):
 * 1. Request passes client-side token check but server returns 401
 * 2. If request is to /auth/refresh-token: logout immediately (no retry)
 * 3. If first 401 (no prior retry):
 *    - Attempt token refresh
 *    - If refresh succeeds: retry original request with _authRetry flag
 *    - If refresh fails: logout and redirect to login
 * 4. If already retried once: logout and redirect to login
 *
 * Notes:
 * - All token expiration logic uses milliseconds precision
 * - Does not intercept requests to the /auth/refresh-token or /auth/login endpoints
 * - Uses _authRetry flag in options to track retry attempts
 * - Uses AuthRetrySuccessError to propagate successful retry results
 *     (see note below)
 *
 * Note on AuthRetrySuccessError:
 *   - ofetch's onResponseError handler does not provide a way to return a successful value
 *     in place of the error; it only allows throwing errors.
 *   - When a retried request succeeds (after a token refresh), we throw an AuthRetrySuccessError
 *     containing the successful response data.
 *   - The consumer (e.g., useSecuredApi) catches this specific error and unwraps the data,
 *     treating it as a successful response.
 *   - This pattern is necessary because ofetch does not support transforming an error into
 *     a success response directly from onResponseError.
 */

import type { FetchContext, FetchError, FetchResponse } from 'ofetch'
import { AuthRetrySuccessError, type AuthFetchOptions } from '~/types/auth'
import { executeAuthFailureHandler } from '~/utils/auth.session-handler'

// Re-export for convenience (backward compatibility)
export { AuthRetrySuccessError, type AuthFetchOptions } from '~/types/auth'

/**
 * Helper function to handle logout and redirect with debouncing
 * Prevents multiple simultaneous logout attempts and error messages
 * Uses shared debounce mechanism from auth.session-handler
 */
async function handleAuthFailure(
  authStore: ReturnType<typeof useAuthStore>
): Promise<void> {
  await executeAuthFailureHandler(async () => {
    // Log the auth failure for debugging
    console.error('Session expired - redirecting to login')

    // Show a single, user-friendly toast message
    const toast = useToast()
    toast.add({
      title: 'Session expired',
      description: 'Your session has expired. Please log in again.',
      color: 'amber',
    })

    // Clear auth state
    authStore.clearState()

    // Navigate to login
    await navigateTo('/auth/login')
  })
}

/**
 * Reset the auth failure flag (exported for testing purposes)
 */
export function _resetAuthFailureFlag(): void {
  // Re-export from shared utility for backward compatibility with tests
  const { resetAuthFailureFlag } = require('~/utils/auth.session-handler')
  resetAuthFailureFlag()
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()
  const apiUrl = config.public.apiUrl

  // Get Pinia instance for proper store access
  const pinia = nuxtApp.$pinia

  // Create a global fetch interceptor
  const $customFetch = $fetch.create({
    // Always include credentials for cookie-based authentication
    credentials: 'include',

    async onRequest(context: FetchContext) {
      // Only intercept requests to our API
      const requestUrl = context.request.toString()
      if (!requestUrl.startsWith(apiUrl)) {
        return
      }

      // Skip interception for auth endpoints that don't need token validation
      const skipEndpoints = [
        '/auth/login',
        '/auth/refresh-token',
        '/auth/logout',
      ]
      const isSkippedEndpoint = skipEndpoints.some(endpoint =>
        requestUrl.includes(endpoint)
      )
      if (isSkippedEndpoint) {
        return
      }

      // Get the auth store (only on client side)
      if (!import.meta.client) {
        return
      }

      const authStore = useAuthStore(pinia)

      // Check if token is expiring soon (within 5 minutes)
      if (authStore.isTokenExpiringSoon && authStore.isAuthenticated) {
        try {
          // Attempt to refresh the token
          await authStore.refresh()
        } catch (error) {
          // Refresh failed - handle auth failure with toast
          console.error('Token refresh failed:', error)
          await handleAuthFailure(authStore)
          // Abort the original request by throwing an error
          throw new Error('Authentication required')
        }
      }
    },

    async onResponseError(
      context: FetchContext & { response: FetchResponse<unknown> }
    ) {
      // Only handle 401 responses
      if (context.response?.status !== 401) {
        return
      }

      // Only handle on client side
      if (!import.meta.client) {
        return
      }

      const authStore = useAuthStore(pinia)
      const requestUrl = context.request.toString()
      const options = context.options as AuthFetchOptions

      // Skip interception for auth endpoints that don't need token validation
      const skipEndpoints = [
        '/auth/login',
        '/auth/refresh-token',
        '/auth/logout',
      ]
      const isSkippedEndpoint = skipEndpoints.some(endpoint =>
        requestUrl.includes(endpoint)
      )
      if (isSkippedEndpoint) {
        await handleAuthFailure(authStore)
        return
      }

      // Skip 401 handling for requests that opted out (e.g., during session restoration)
      // The caller is responsible for handling the error itself
      if (options._skipAuthRetry) {
        return
      }

      // Check if we've already retried this request - logout without further retry
      if (options._authRetry) {
        await handleAuthFailure(authStore)
        return
      }

      // First 401: attempt to refresh token and retry the request
      try {
        // Attempt to refresh the token
        await authStore.refresh()

        // Token refresh succeeded - retry the original request
        // Create new options with _authRetry flag to prevent infinite retry loops
        const retryOptions: AuthFetchOptions = {
          ...options,
          _authRetry: true,
        }

        // Retry the original request using the same $customFetch
        // This ensures the retry also goes through our interceptor
        const retryResult = await $customFetch(context.request, retryOptions)

        // Throw a special error to indicate successful retry
        // This will be caught by useSecuredApi wrapper and returned as the result
        throw new AuthRetrySuccessError(retryResult)
      } catch (refreshOrRetryError) {
        // Check if this is our success marker - re-throw to propagate to caller
        if (refreshOrRetryError instanceof AuthRetrySuccessError) {
          throw refreshOrRetryError
        }

        // Type the error for proper status code checking
        const fetchError = refreshOrRetryError as FetchError

        // Only logout for auth failures (401), re-throw other errors
        if (fetchError?.status === 401 || fetchError?.statusCode === 401) {
          await handleAuthFailure(authStore)
        } else {
          throw refreshOrRetryError // Let caller handle non-auth errors
        }
      }
    },
  })

  // Provide the custom fetch globally
  return {
    provide: {
      securedFetch: $customFetch,
    },
  }
})
